import React from 'react';

const FocuvoPrivacyPolicyPage = () => {
  return (
    <div className="container py-12 mx-auto">
      <div className="max-w-4xl mx-auto">
        <h1 className="mb-8 text-4xl font-bold text-center text-secondary-900">Focuvo Privacy Policy</h1>

        <div className="prose prose-lg max-w-none">
          <p className="mb-6">
            Last updated: October 5, 2026
          </p>

          <h2 className="mt-8 mb-4 text-2xl font-semibold">1. Introduction</h2>
          <p>
            This Privacy Policy explains how the Focuvo mobile application ("Focuvo," "the app") handles your information. Focuvo is developed and published by FutureSoft Technologies ("we," "our," or "us").
          </p>
          <p>
            Focuvo is designed to work without an account. Your goals, plans, focus sessions and settings are stored only on your device. Signing in is optional, and if you do, we only store the minimum needed to identify your account.
          </p>

          <h2 className="mt-8 mb-4 text-2xl font-semibold">2. Information Stored Only on Your Device</h2>
          <p>
            Most of what Focuvo uses never leaves your phone. We cannot see it, and it is never sent to our servers:
          </p>
          <ul className="ml-6 mb-6 list-disc">
            <li>Goals, deadlines, subjects, milestones and study plans you create</li>
            <li>Your availability, commitments and session length preferences</li>
            <li>Focus session history and progress</li>
            <li>Apps you choose to hold or shield during focus sessions</li>
            <li>The name you enter during setup, theme and other app settings</li>
            <li>App usage statistics read from your device (see Section 4)</li>
          </ul>
          <p>
            This data is removed when you clear the app's storage or uninstall Focuvo. If you have Android device backup turned on, Android may include this data in your device backup with Google. Your sign-in session tokens are excluded from that backup.
          </p>

          <h2 className="mt-8 mb-4 text-2xl font-semibold">3. Information Collected When You Sign In (Optional)</h2>
          <p>
            You can choose to sign in with your Google account. You do not need to sign in to use any feature of the app. If you sign in, we receive and store the following from Google:
          </p>
          <ul className="ml-6 mb-6 list-disc">
            <li>Your Google account identifier</li>
            <li>Your email address</li>
            <li>Your name as shown on your Google account</li>
          </ul>
          <p>
            We use this information only to create and identify your Focuvo account and to show your name and email in the app. Your goals, plans, sessions and usage data are not uploaded to your account.
          </p>

          <h2 className="mt-8 mb-4 text-2xl font-semibold">4. Device Permissions</h2>
          <p>
            Focuvo asks for the following permissions. Each one is optional and explained in the app before it is requested:
          </p>
          <ul className="ml-6 mb-6 list-disc">
            <li><strong>Usage access:</strong> shows your screen time and detects when a distracting app is opened during a focus session. This data is processed on your device only and is never transmitted.</li>
            <li><strong>Installed apps list:</strong> lets you choose which apps count as distractions. The list is read on your device and is never transmitted.</li>
            <li><strong>Display over other apps:</strong> shows a gentle check-in screen when you open a distracting app during a session.</li>
            <li><strong>Do Not Disturb access:</strong> optionally silences interruptions while you focus.</li>
            <li><strong>Notifications:</strong> shows the running session timer, reminders and progress updates.</li>
            <li><strong>Foreground service and run at startup:</strong> keeps an active focus session or shield running, including after your phone restarts.</li>
            <li><strong>Internet:</strong> used only for optional Google sign-in and account management.</li>
          </ul>

          <h2 className="mt-8 mb-4 text-2xl font-semibold">5. How We Use Your Information</h2>
          <p>
            We use the limited account information described above only to:
          </p>
          <ul className="ml-6 mb-6 list-disc">
            <li>Sign you in and keep you signed in</li>
            <li>Show your name and email in the app</li>
            <li>Delete your account when you ask us to</li>
          </ul>
          <p>
            We do not sell your data. We do not use it for advertising. We do not use it to track you across other apps or websites. Focuvo contains no advertising, analytics or third-party tracking SDKs.
          </p>

          <h2 className="mt-8 mb-4 text-2xl font-semibold">6. Third-Party Services</h2>
          <p>
            If you choose to sign in, Focuvo relies on these service providers:
          </p>
          <ul className="ml-6 mb-6 list-disc">
            <li><strong>Google Sign-In</strong> (Google LLC) to verify your identity. Google's use of your information is described in the <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">Google Privacy Policy</a>.</li>
            <li><strong>Supabase</strong> (Supabase, Inc.) to host account authentication and storage. See the <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">Supabase Privacy Policy</a>.</li>
          </ul>
          <p>
            We may also disclose information to legal authorities when required by law.
          </p>

          <h2 className="mt-8 mb-4 text-2xl font-semibold">7. Data Security</h2>
          <p>
            All data sent between the app and our servers is encrypted in transit using HTTPS. Account records are protected by access rules so that each signed-in user can only read their own profile. However, no method of transmission or storage is 100% secure.
          </p>

          <h2 className="mt-8 mb-4 text-2xl font-semibold">8. Data Retention</h2>
          <p>
            Account information is kept for as long as your account exists. It is permanently deleted when you delete your account. Signing out does not delete your account or the data on your device.
          </p>

          <h2 id="delete-account" className="mt-8 mb-4 text-2xl font-semibold">9. Deleting Your Account and Data</h2>
          <p>
            You can delete your Focuvo account at any time from within the app:
          </p>
          <ol className="ml-6 mb-6 list-decimal">
            <li>Open Focuvo and go to <strong>Settings</strong></li>
            <li>Under <strong>Account</strong>, tap <strong>Delete account</strong></li>
            <li>Confirm by tapping <strong>Delete</strong></li>
          </ol>
          <p>
            This permanently deletes your account and the associated profile (Google account identifier, email address and name) from our servers. No account data is retained after deletion.
          </p>
          <p>
            If you no longer have the app installed, you can ask us to delete your account. Email <a href="mailto:privacy@futuresoft.tech?subject=Focuvo%20account%20deletion" className="text-primary-600 hover:underline">privacy@futuresoft.tech</a> from the email address linked to your account with the subject "Focuvo account deletion." We will process your request within 30 days.
          </p>
          <p>
            To remove the data stored on your device, clear Focuvo's storage in your phone's settings or uninstall the app.
          </p>

          <h2 className="mt-8 mb-4 text-2xl font-semibold">10. Your Rights</h2>
          <p>
            Depending on your location, you may have rights to access, correct, delete or export the personal information we hold about you, or to object to how we process it. To exercise these rights, please contact us using the details below.
          </p>

          <h2 className="mt-8 mb-4 text-2xl font-semibold">11. Children's Privacy</h2>
          <p>
            Focuvo is not directed to children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has created an account, please contact us and we will delete it.
          </p>

          <h2 className="mt-8 mb-4 text-2xl font-semibold">12. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. When we do, we will post the new version on this page and update the "Last updated" date. Significant changes may also be announced in the app.
          </p>

          <h2 className="mt-8 mb-4 text-2xl font-semibold">13. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or Focuvo, please contact us at:
          </p>
          <p className="mb-6">
            FutureSoft Technologies<br />
            Email: privacy@futuresoft.tech<br />
            Phone: +91 9455544334<br />
            Address: 12, Block C8, 1st Floor, Sector 18, Vrindavan yojna, Lucknow, Uttar Pradesh
          </p>
        </div>
      </div>
    </div>
  );
};

export default FocuvoPrivacyPolicyPage;
